import React, { useState, useEffect } from 'react';
import { Page, Frame, Modal } from '@shopify/polaris';
import AppBridgeSaveBar from '../../components/AppBridgeSaveBar';
import { useQuery, useMutation } from '@apollo/client';
import AnnouncementSettings from '../../features/AnnoncementBar/AnnouncementSettings';
import AnnouncementSaleOrderModal from '../../features/AnnoncementBar/AnnouncementSaleOrderModal';
import { GET_DESIGN_SETTINGS } from '../../graphql/Queries/Settings';
import { SAVE_ANNOUNCEMENT_SETTINGS } from "../../graphql/Mutations/Settings";
import { isEqual } from 'lodash';
import { ArrowLeftMinor } from '@shopify/polaris-icons';
import WidgetDesignSkeletonPage from './WidgetDesignSkeletonPage';
import UnsavedChangesDialog from '../../components/Modals/UnsavedChangesDialog';
import { useAppBridge } from '@shopify/app-bridge-react';

const AnnouncementBarPage = ({ 
  appName = "discount-planner-by-productessentials",
  dialog = false,
  open = false,
  onClose = () => {},
  onBack = () => {}
}) => {
  const { loading, data } = useQuery(GET_DESIGN_SETTINGS(appName), {
    fetchPolicy: "network-only",
  });

  const shopify = useAppBridge();

  const [settings, setSettings] = useState();
  const [initialSettings, setInitialSettings] = useState();
  const [saveSettings, { loading: saving }] = useMutation(SAVE_ANNOUNCEMENT_SETTINGS(appName));
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [sales, setSales] = useState([]);
  const [saleOrderOpen, setSaleOrderOpen] = useState(false);

  // Set settings when data loads
  useEffect(() => {
    if (data && data.settings && data.settings.AnnouncementBarSettings) {
      setSettings(data.settings.AnnouncementBarSettings);
      setInitialSettings(data.settings.AnnouncementBarSettings);
      setSales(data.settings.AnnouncementSales || []);
    }
  }, [data]);

  if (loading && !dialog) return <WidgetDesignSkeletonPage title="Announcement bar settings" />;

  const handleClose = () => {
    if (dialog && !saveDisabled) {
      setLeaveDialogOpen(true);
    } else {
      setSettings(initialSettings);
      onClose();
    }
  };
  const handleBack = async () => {
    // When not in dialog mode, just call onBack - the AppBridgeSaveBar will indicate unsaved changes and shake if needed
    await shopify.saveBar.leaveConfirmation();
    onBack();
  };

  const handleSave = async (settingsToSave) => {
    try {
      await saveSettings({
        variables: {
          input: {
            Settings: settingsToSave,
          },
        },
      });
      setSettings(settingsToSave);
      setInitialSettings(settingsToSave);
      shopify.toast.show("Announcement settings saved", { duration: 1500 });
    } catch (err) {
      console.error(err);
      shopify.toast.show(`Error saving settings: ${err.message}`, { duration: 1500, isError: true });
    }
  };
  const saveDisabled = isEqual(initialSettings, settings);
  const content = (
    <AnnouncementSettings 
      settings={settings}
      onSettingsChange={setSettings}
      appName={appName}
      onShowToast={(message) => {
        shopify.toast.show(message, { duration: 1500 });
      }}
    />
  );

  if (dialog) {
    return (
      <>
        <Modal
          open={open}
          size="large"
          onClose={handleClose}
          title="Announcement bar settings"
          primaryAction={{
            content: 'Save',
            onAction: async () => { 
              await handleSave(settings); 
              onClose(); 
            },
            disabled: saveDisabled,
            loading: saving
          }}
        >
          <Modal.Section>{content}</Modal.Section>
        </Modal>
        <UnsavedChangesDialog
          open={leaveDialogOpen}
          onStay={() => setLeaveDialogOpen(false)}
          onLeave={() => { setSettings(initialSettings); setLeaveDialogOpen(false); onClose(); }}
          message="You have unsaved changes. Changes will be lost if you leave without saving."
        />
      </>
    );
  }

  return (
    <>
      <Frame>
        <AppBridgeSaveBar
          id="announcement-bar-save-bar"
          open={!saveDisabled}
          blockNavigation={!saveDisabled}
          saveAction={{
            onAction: () => handleSave(settings),
            loading: saving,
          }}
          discardAction={{
            onAction: () => setSettings(initialSettings),
          }}
        />
        <Page 
          title="Announcement bar settings" 
          primaryAction={{ 
            content: 'Save', 
            onAction: () => handleSave(settings), 
            disabled: saveDisabled, 
            loading: saving 
          }}
          secondaryActions={sales.length > 1 ? [{ content: 'Sale order', onAction: () => setSaleOrderOpen(true) }] : []}
          backAction={{
            content: 'Back',
            icon: ArrowLeftMinor,
            onAction: handleBack
          }}
        >
          {content}
        </Page>
        <AnnouncementSaleOrderModal
          appName={appName}
          open={saleOrderOpen}
          sales={sales}
          onClose={() => setSaleOrderOpen(false)}
          onSaved={setSales}
        />
      </Frame>
    </>
  );
};

export default AnnouncementBarPage;
